/**
 * ErrorBoundary global (R19) — captura qualquer throw na render tree do React
 * e mostra fallback amigável em vez de tela branca.
 *
 * Render tree do React inteira fica protegida quando wrap _app.tsx neste
 * componente. Erros server-side (SSR) ainda caem em pages/_error.tsx do Next.
 *
 * Não usa o `useTranslation` pra evitar dependência circular (se o erro
 * vier do próprio i18n, o boundary precisa funcionar mesmo assim).
 *
 * Reporta no Sentry se window.Sentry estiver disponível.
 */
import React, { Component, ErrorInfo, ReactNode } from "react";
import i18n from "i18next";  // R29_FINAL_I18N

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Loga no console pra desenvolvimento.
    console.error("[ErrorBoundary] React tree error:", error);
    console.error("[ErrorBoundary] componentStack:", errorInfo.componentStack);

    // Reporta no Sentry se disponível.
    // O Sentry SDK do browser geralmente já captura erros não tratados, mas
    // erros pegos por ErrorBoundary NÃO chegam no handler global — daí o
    // capture explícito.
    type SentryGlobal = {
      Sentry?: {
        captureException?: (err: unknown, ctx?: unknown) => void;
      };
    };
    const sentry = (window as unknown as SentryGlobal).Sentry;
    if (sentry?.captureException) {
      try {
        sentry.captureException(error, {
          contexts: {
            react: { componentStack: errorInfo.componentStack },
          },
        });
      } catch {
        // ignora — não vamos quebrar o boundary por causa do Sentry
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errorMsg = this.state.error?.message ?? "Erro desconhecido";

    return (
      <div className="min-h-screen flex items-center justify-center bg-app px-4">
        <div className="bg-app-surface rounded-2xl shadow-sm border border-app max-w-lg w-full p-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.732 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-serif text-xl text-app-1 mb-1">{i18n.t("common.somethingWrong", "Algo deu errado")}</h1>
              <p className="text-sm text-app-3 mb-4">
                {i18n.t("errors.boundaryBody", "Encontramos um erro inesperado. A equipe foi notificada — você pode tentar de novo ou recarregar a página.")}
              </p>

              <details className="mb-4 text-xs">
                <summary className="cursor-pointer text-app-muted hover:text-app-2 font-mono">
                  {i18n.t("common.technicalDetails", "Detalhes técnicos")}
                </summary>
                <pre className="mt-2 p-3 bg-app-soft rounded-lg overflow-x-auto text-app-3 font-mono whitespace-pre-wrap break-all">
                  {errorMsg}
                </pre>
              </details>

              <div className="flex gap-2">
                <button
                  onClick={this.handleReset}
                  className="px-4 py-2 rounded-xl text-sm font-medium border border-app bg-app-surface hover:bg-app-hover text-app-2"
                >
                  {i18n.t("common.tryAgain", "Tentar de novo")}
                </button>
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 rounded-xl text-sm font-medium bg-app-1 text-app-surface hover:opacity-90"
                  style={{ background: "var(--text-1)", color: "var(--surface)" }}
                >
                  {i18n.t("common.reloadPage", "Recarregar página")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
